import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DndContext, DragEndEvent, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Pencil, Trash2, LogOut, ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, Maximize2, ArrowLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageGrid from "./admin/ImageGrid";

interface Category {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface Job {
  id: string;
  category_id: string;
  title: string;
  description: string | null;
  slug: string;
  sort_order: number;
  image_layout: string;
}

interface Image {
  id: string;
  job_id: string;
  url: string;
  caption: string | null;
  sort_order: number;
  show_on_home: boolean;
  home_order: number | null;
}

type MoveDirection = "up" | "down" | "top" | "bottom";

const SortableItem = ({
  id,
  children,
  onMove,
  isFirst,
  isLast,
}: {
  id: string;
  children: React.ReactNode;
  onMove?: (direction: MoveDirection) => void;
  isFirst?: boolean;
  isLast?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 p-3 bg-card border rounded-md mb-2 ${isDragging ? "shadow-lg ring-2 ring-primary relative z-10" : ""}`}
    >
      {/* Stor handtag-yta: lätt att ta tag i, även på mobil/platta */}
      <div
        {...attributes}
        {...listeners}
        title="Dra för att flytta"
        className="flex items-center justify-center self-stretch min-w-[44px] -my-3 -ml-3 px-3 rounded-l-md cursor-grab active:cursor-grabbing touch-none hover:bg-muted"
      >
        <GripVertical className="w-6 h-6 text-muted-foreground" />
      </div>
      {children}
      {onMove && (
        <div className="flex flex-col gap-0.5 shrink-0">
          <div className="flex gap-0.5">
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" title="Flytta först" disabled={isFirst} onClick={() => onMove("top")}>
              <ChevronsUp className="w-4 h-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" title="Flytta upp ett steg" disabled={isFirst} onClick={() => onMove("up")}>
              <ArrowUp className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-0.5">
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" title="Flytta ner ett steg" disabled={isLast} onClick={() => onMove("down")}>
              <ArrowDown className="w-4 h-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" title="Flytta sist" disabled={isLast} onClick={() => onMove("bottom")}>
              <ChevronsDown className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const moveInList = <T,>(list: T[], index: number, direction: MoveDirection): T[] => {
  const target =
    direction === "top" ? 0 :
    direction === "bottom" ? list.length - 1 :
    direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= list.length || target === index) return list;
  return arrayMove(list, index, target);
};

const Admin = () => {
  useEffect(() => {
    document.title = "Admin - Ina Nederdal";
  }, []);

  const navigate = useNavigate();
  // Dra först när man flyttat musen 5px, så vanliga klick fortfarande fungerar.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'categories' | 'home'>('categories');
  
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategorySlug, setNewCategorySlug] = useState("");
  
  const [editingJob, setEditingJob] = useState<string | null>(null);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newJobDescription, setNewJobDescription] = useState("");
  const [newJobSlug, setNewJobSlug] = useState("");
  const [newJobCategoryId, setNewJobCategoryId] = useState("");
  
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageCaption, setNewImageCaption] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingImageCaption, setEditingImageCaption] = useState<string | null>(null);
  const [savingJob, setSavingJob] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Add timeout for loading state
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  useEffect(() => {
    let isMounted = true;
    
    // Helper function to check admin role
    const checkAdminRole = async (userId: string) => {
      console.log('[Admin] Checking admin role for user:', userId);
      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .eq("role", "admin")
          .maybeSingle();
        
        console.log('[Admin] Role check result:', { data, error });
        return data && !error;
      } catch (err) {
        console.error('[Admin] Error checking admin role:', err);
        return false;
      }
    };

    // Check authentication status and admin role
    const checkAuth = async () => {
      console.log('[Admin] Checking authentication...');
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[Admin] Error getting session:', error);
          if (isMounted) setLoading(false);
          return;
        }
        
        console.log('[Admin] Session:', session?.user?.email);
        
        if (session?.user) {
          if (isMounted) setUser(session.user);
          
          const isAdminUser = await checkAdminRole(session.user.id);
          
          if (isMounted) {
            if (isAdminUser) {
              console.log('[Admin] User is admin, loading data...');
              setIsAdmin(true);
              try {
                await loadData();
              } catch (loadErr) {
                console.error('[Admin] Error loading data:', loadErr);
              }
            } else {
              console.log('[Admin] User is not admin');
              setIsAdmin(false);
            }
          }
        } else {
          console.log('[Admin] No session found');
        }
      } catch (err) {
        console.error('[Admin] Error in checkAuth:', err);
      } finally {
        // Always set loading to false, regardless of isMounted
        // This ensures we don't get stuck in loading state
        console.log('[Admin] Setting loading to false');
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    // Listen for auth changes - CRITICAL: Never use async or call Supabase inside this callback directly
    // to avoid deadlocks. Use setTimeout(0) to defer Supabase calls.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Admin] Auth state changed:', event);
      
      // Skip INITIAL_SESSION as we handle it in checkAuth
      if (event === 'INITIAL_SESSION') {
        return;
      }
      
      // Only synchronous state updates here
      if (session?.user) {
        if (isMounted) setUser(session.user);
        
        // Defer Supabase calls with setTimeout to prevent deadlock
        setTimeout(async () => {
          if (!isMounted) return;
          
          try {
            const isAdminUser = await checkAdminRole(session.user.id);
            
            if (isMounted) {
              setIsAdmin(isAdminUser);
              if (isAdminUser) {
                console.log('[Admin] Loading data after auth change');
                await loadData();
              }
              setLoading(false);
            }
          } catch (err) {
            console.error('[Admin] Error in deferred auth handler:', err);
            if (isMounted) setLoading(false);
          }
        }, 0);
      } else {
        console.log('[Admin] No session in auth change');
        if (isMounted) {
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast.error("Login failed: " + error.message);
    }
    
    // Always set loading to false after login attempt
    setLoading(false);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      console.error('Logout error:', error);
      toast.error("Logout failed: Please try again");
    } finally {
      setLoggingOut(false);
    }
  };

  const loadData = async () => {
    try {
      const { data: cats, error: catsError } = await supabase
        .from("categories")
        .select("id, name, slug, sort_order, created_at")
        .order("sort_order");
      if (catsError) console.error('[Admin] Error loading categories:', catsError);
      if (cats) setCategories(cats);

      const { data: jbs, error: jobsError } = await supabase
        .from("jobs")
        .select("*")
        .order("sort_order");
      if (jobsError) console.error('[Admin] Error loading jobs:', jobsError);
      if (jbs) setJobs(jbs);

      const { data: imgs, error: imgsError } = await supabase
        .from("images")
        .select("*")
        .order("sort_order");
      if (imgsError) console.error('[Admin] Error loading images:', imgsError);
      if (imgs) setImages(imgs);
    } catch (err) {
      console.error('[Admin] Error in loadData:', err);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim() || !newCategorySlug.trim()) return;
    
    // Validate slug format (lowercase, hyphens, no spaces)
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(newCategorySlug)) {
      toast.error("Invalid slug: Slug must be lowercase letters, numbers, and hyphens only");
      return;
    }
    
    const { error } = await supabase
      .from("categories")
      .insert({ 
        name: newCategoryName, 
        slug: newCategorySlug,
        sort_order: categories.length 
      });
    
    if (!error) {
      setNewCategoryName("");
      setNewCategorySlug("");
      loadData();
      toast.success(`Category added. Create folder: public/assets/${newCategorySlug}/`);
    }
  };

  const updateCategory = async (id: string, name: string) => {
    const { error } = await supabase
      .from("categories")
      .update({ name })
      .eq("id", id);
    
    if (!error) {
      setEditingCategory(null);
      loadData();
      toast.success("Category updated");
    }
  };

  const deleteCategory = async (id: string) => {
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (!error) {
      loadData();
      toast.success("Category deleted");
    }
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const addJob = async () => {
    if (!selectedCategory || !newJobTitle.trim()) return;
    
    // Auto-generate slug from title if not provided
    const jobSlug = newJobSlug.trim() || generateSlug(newJobTitle);
    
    const { error } = await supabase
      .from("jobs")
      .insert({
        category_id: selectedCategory,
        title: newJobTitle,
        description: newJobDescription || null,
        slug: jobSlug,
        sort_order: jobs.filter(j => j.category_id === selectedCategory).length
      });
    
    if (!error) {
      setNewJobTitle("");
      setNewJobDescription("");
      setNewJobSlug("");
      loadData();
      toast.success("Job added");
    }
  };

  const updateJob = async (id: string, title: string, slug: string, description: string, categoryId: string, imageLayout: string) => {
    const job = jobs.find(j => j.id === id);
    if (!job) return;
    
    setSavingJob(id);
    
    try {
      const oldCategoryId = job.category_id;
      const categoryChanged = oldCategoryId !== categoryId;
      
      // Images keep their existing files/URLs; only the job's category changes.

      const { error } = await supabase
        .from("jobs")
        .update({ title, slug, description, category_id: categoryId, image_layout: imageLayout })
        .eq("id", id);
      
      if (error) {
        throw error;
      }
      
      setEditingJob(null);
      setNewJobCategoryId("");
      await loadData();
      if (categoryChanged) {
        const newCatName = categories.find(c => c.id === categoryId)?.name;
        toast.success(`Job moved to ${newCatName}`);
      } else {
        toast.success("Job updated");
      }
    } catch (error) {
      console.error('Update job error:', error);
      toast.error("Failed to update job: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setSavingJob(null);
    }
  };

  const deleteJob = async (id: string) => {
    const { error } = await supabase.from("jobs").delete().eq("id", id);
    if (!error) {
      loadData();
      toast.success("Job deleted");
    }
  };

  const processImageFiles = async (files: File[]) => {
    if (!selectedJob || files.length === 0) return;
    
    setUploadingImage(true);
    
    try {
      const job = jobs.find(j => j.id === selectedJob);
      if (!job) throw new Error("Job not found");

      const { data: categoryData } = await supabase
        .from('categories')
        .select('slug')
        .eq('id', job.category_id)
        .single();

      if (!categoryData) throw new Error("Category not found");

      const categorySlug = categoryData.slug;
      
      // Give every uploaded image its own sort position (in the order the files were chosen)
      const baseOrder = images.filter(i => i.job_id === selectedJob).length;
      let uploadIndex = 0;

      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${categorySlug}/${fileName}`;
        
        // Upload to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('portfolio-images')
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('portfolio-images')
          .getPublicUrl(filePath);
        
        const captionFromFilename = file.name.replace(/\.[^/.]+$/, "");
        
        const { error: insertError } = await supabase
          .from("images")
          .insert({
            job_id: selectedJob,
            url: publicUrl,
            caption: captionFromFilename,
            sort_order: baseOrder + uploadIndex
          });
        uploadIndex++;
        
        if (insertError) throw insertError;
      }
      
      loadData();
      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error("Failed to upload image: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedJob || !e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    await processImageFiles(files);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (!selectedJob) {
      toast.error("Select a job first");
      return;
    }
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length === 0) {
      toast.error("No image files found");
      return;
    }
    
    await processImageFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const addImage = async () => {
    if (!selectedJob || !newImageUrl.trim()) return;
    
    // Extract caption from URL filename if not provided
    let caption = newImageCaption;
    if (!caption && newImageUrl.includes('/')) {
      const urlParts = newImageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      caption = fileName.replace(/\.[^/.]+$/, "");
    }
    
    const { error } = await supabase
      .from("images")
      .insert({
        job_id: selectedJob,
        url: newImageUrl,
        caption: caption || null,
        sort_order: images.filter(i => i.job_id === selectedJob).length
      });
    
    if (!error) {
      setNewImageUrl("");
      setNewImageCaption("");
      loadData();
      toast.success("Image added");
    }
  };

  const updateImageCaption = async (id: string, caption: string) => {
    const { error } = await supabase
      .from("images")
      .update({ caption })
      .eq("id", id);
    
    if (!error) {
      setEditingImageCaption(null);
      loadData();
      toast.success("Caption updated");
    }
  };

  const deleteImage = async (id: string) => {
    const { error } = await supabase.from("images").delete().eq("id", id);
    if (!error) {
      loadData();
      toast.success("Image deleted");
    }
  };

  const toggleHomeImage = async (id: string, showOnHome: boolean) => {
    const updates: any = { show_on_home: showOnHome };
    
    if (showOnHome) {
      // Assign next home_order
      const homeImages = images.filter(i => i.show_on_home);
      updates.home_order = homeImages.length;
    } else {
      updates.home_order = null;
    }
    
    const { error } = await supabase
      .from("images")
      .update(updates)
      .eq("id", id);
    
    if (!error) {
      loadData();
      toast.success(showOnHome ? "Added to home" : "Removed from home");
    }
  };

  // Sparar ny ordning direkt i skärmen (snabbt) och sedan i databasen.
  const applyImageOrder = async (newOrder: Image[], field: "sort_order" | "home_order") => {
    const positions = new Map(newOrder.map((img, i) => [img.id, i]));
    setImages(prev => {
      const updated = prev.map(img => (positions.has(img.id) ? { ...img, [field]: positions.get(img.id)! } : img));
      return field === "sort_order" ? [...updated].sort((a, b) => a.sort_order - b.sort_order) : updated;
    });

    const results = await Promise.all(
      newOrder.map((img, i) => supabase.from("images").update({ [field]: i }).eq("id", img.id))
    );
    if (results.some(r => r.error)) toast.error("Kunde inte spara ordningen");
    loadData();
  };

  const getHomeImages = () =>
    images.filter(i => i.show_on_home).sort((a, b) => (a.home_order || 0) - (b.home_order || 0));

  const handleHomeDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const homeImages = getHomeImages();
    const oldIndex = homeImages.findIndex(i => i.id === active.id);
    const newIndex = homeImages.findIndex(i => i.id === over.id);
    await applyImageOrder(arrayMove(homeImages, oldIndex, newIndex), "home_order");
  };

  const moveHomeImage = async (id: string, direction: MoveDirection) => {
    const homeImages = getHomeImages();
    const index = homeImages.findIndex(i => i.id === id);
    await applyImageOrder(moveInList(homeImages, index, direction), "home_order");
  };

  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(categories, oldIndex, newIndex);
    
    setCategories(newOrder);
    
    for (let i = 0; i < newOrder.length; i++) {
      await supabase
        .from("categories")
        .update({ sort_order: i })
        .eq("id", newOrder[i].id);
    }
  };

  const handleJobDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const categoryJobs = jobs.filter(j => j.category_id === selectedCategory);
    const oldIndex = categoryJobs.findIndex(j => j.id === active.id);
    const newIndex = categoryJobs.findIndex(j => j.id === over.id);
    const newOrder = arrayMove(categoryJobs, oldIndex, newIndex);
    
    for (let i = 0; i < newOrder.length; i++) {
      await supabase
        .from("jobs")
        .update({ sort_order: i })
        .eq("id", newOrder[i].id);
    }
    
    loadData();
  };

  const handleImageDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const jobImages = images.filter(i => i.job_id === selectedJob);
    const oldIndex = jobImages.findIndex(i => i.id === active.id);
    const newIndex = jobImages.findIndex(i => i.id === over.id);
    await applyImageOrder(arrayMove(jobImages, oldIndex, newIndex), "sort_order");
  };

  const moveJobImage = async (id: string, direction: MoveDirection) => {
    const jobImages = images.filter(i => i.job_id === selectedJob);
    const index = jobImages.findIndex(i => i.id === id);
    await applyImageOrder(moveInList(jobImages, index, direction), "sort_order");
  };

  // Byter ut själva bildfilen men behåller namn, ordning och startsides-val.
  const replaceImage = async (id: string, file: File) => {
    try {
      const img = images.find(i => i.id === id);
      const job = jobs.find(j => j.id === img?.job_id);
      const cat = categories.find(c => c.id === job?.category_id);
      if (!img || !cat) throw new Error("Hittar inte bilden");

      const fileExt = file.name.split('.').pop();
      const filePath = `${cat.slug}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('portfolio-images').upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('portfolio-images').getPublicUrl(filePath);
      const { error } = await supabase.from("images").update({ url: publicUrl }).eq("id", id);
      if (error) throw error;

      setImages(prev => prev.map(i => (i.id === id ? { ...i, url: publicUrl } : i)));
      toast.success("Bilden är utbytt");
    } catch (error) {
      toast.error("Kunde inte byta bild: " + (error instanceof Error ? error.message : "Okänt fel"));
    }
  };

  const reorderImagesById = async (activeId: string, overId: string) => {
    const jobImages = images.filter(i => i.job_id === selectedJob);
    const oldIndex = jobImages.findIndex(i => i.id === activeId);
    const newIndex = jobImages.findIndex(i => i.id === overId);
    if (oldIndex < 0 || newIndex < 0) return;
    await applyImageOrder(arrayMove(jobImages, oldIndex, newIndex), "sort_order");
  };

  const saveCaption = async (id: string, caption: string) => {
    const { error } = await supabase.from("images").update({ caption }).eq("id", id);
    if (error) {
      toast.error("Kunde inte spara namnet");
      return;
    }
    setImages(prev => prev.map(i => (i.id === id ? { ...i, caption } : i)));
  };

  const imageGridEl = (large: boolean) => (
    <ImageGrid
      images={images.filter(i => i.job_id === selectedJob)}
      layout={jobs.find(j => j.id === selectedJob)?.image_layout || "single"}
      onReorder={reorderImagesById}
      onDelete={deleteImage}
      onReplace={replaceImage}
      onCaptionSave={saveCaption}
      large={large}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="text-muted-foreground">Loading...</div>
        {loadingTimeout && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Taking longer than expected...</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-96 p-6">
          <h1 className="text-2xl font-semibold mb-6 text-foreground">Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-96 p-6 text-center">
          <h1 className="text-2xl font-semibold mb-4 text-foreground">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You do not have admin privileges to access this page.
          </p>
          <Button onClick={handleLogout} variant="outline" className="w-full">
            Sign Out
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading">Portfolio Admin</h1>
          <Button variant="outline" onClick={handleLogout} disabled={loggingOut}>
            <LogOut className="w-4 h-4 mr-2" />
            {loggingOut ? "Logging out..." : "Logout"}
          </Button>
        </div>

        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'categories' ? 'default' : 'outline'}
            onClick={() => setActiveTab('categories')}
          >
            Categories & Jobs
          </Button>
          <Button 
            variant={activeTab === 'home' ? 'default' : 'outline'}
            onClick={() => setActiveTab('home')}
          >
            Home Images
          </Button>
        </div>

        {activeTab === 'categories' && (
          <>
          {!selectedJob && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Categories */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Categories</h2>
            
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
              <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                {categories.map(cat => (
                  <SortableItem key={cat.id} id={cat.id}>
                    {editingCategory === cat.id ? (
                      <Input
                        value={cat.name}
                        onChange={(e) => {
                          const updated = categories.map(c => 
                            c.id === cat.id ? { ...c, name: e.target.value } : c
                          );
                          setCategories(updated);
                        }}
                        onBlur={() => updateCategory(cat.id, cat.name)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") updateCategory(cat.id, cat.name);
                        }}
                        className="flex-1"
                        autoFocus
                      />
                    ) : (
                      <>
                        <button
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`flex-1 text-left ${selectedCategory === cat.id ? "font-semibold" : ""}`}
                        >
                          <div>{cat.name}</div>
                          <div className="text-xs text-muted-foreground">
                            public/assets/{cat.slug}/
                          </div>
                        </button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingCategory(cat.id)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteCategory(cat.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>

            <div className="mt-4 space-y-2">
              <Input
                placeholder="Category name (e.g., Editorial)"
                value={newCategoryName}
                onChange={(e) => {
                  setNewCategoryName(e.target.value);
                  // Auto-generate slug from name
                  setNewCategorySlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^a-z0-9-]/g, '')
                  );
                }}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
              />
              <Input
                placeholder="Folder slug (e.g., editorial)"
                value={newCategorySlug}
                onChange={(e) => setNewCategorySlug(e.target.value.toLowerCase())}
                onKeyDown={(e) => e.key === "Enter" && addCategory()}
              />
              <Button onClick={addCategory} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>
          </Card>

          {/* Jobs */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">
              Jobs {selectedCategory && `- ${categories.find(c => c.id === selectedCategory)?.name}`}
            </h2>
            
            {selectedCategory ? (
              <>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleJobDragEnd}>
                  <SortableContext 
                    items={jobs.filter(j => j.category_id === selectedCategory).map(j => j.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    {jobs.filter(j => j.category_id === selectedCategory).map(job => (
                      <SortableItem key={job.id} id={job.id}>
                        {editingJob === job.id ? (
                          <div className="flex-1 space-y-2">
                            <Input
                              value={job.title}
                              onChange={(e) => {
                                const updated = jobs.map(j => 
                                  j.id === job.id ? { ...j, title: e.target.value } : j
                                );
                                setJobs(updated);
                              }}
                              placeholder="Job title"
                              className="text-sm"
                            />
                            <Select
                              value={newJobCategoryId || job.category_id}
                              onValueChange={(value) => setNewJobCategoryId(value)}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(cat => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={job.slug}
                              onChange={(e) => {
                                const updated = jobs.map(j => 
                                  j.id === job.id ? { ...j, slug: e.target.value } : j
                                );
                                setJobs(updated);
                              }}
                              placeholder="Slug"
                              className="text-sm"
                            />
                            <Textarea
                              value={job.description || ""}
                              onChange={(e) => {
                                const updated = jobs.map(j => 
                                  j.id === job.id ? { ...j, description: e.target.value } : j
                                );
                                setJobs(updated);
                              }}
                              placeholder="Description"
                              className="text-sm min-h-[60px]"
                            />
                            <Select
                              value={job.image_layout || 'single'}
                              onValueChange={(value) => {
                                const updated = jobs.map(j => 
                                  j.id === job.id ? { ...j, image_layout: value } : j
                                );
                                setJobs(updated);
                              }}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Image layout" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="single">Single column (default)</SelectItem>
                                <SelectItem value="grid-2">Two column grid</SelectItem>
                                <SelectItem value="grid-3">Three column grid</SelectItem>
                                <SelectItem value="mixed">Mixed (alternating 2/3 columns)</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => updateJob(
                                job.id, 
                                job.title, 
                                job.slug, 
                                job.description || "", 
                                newJobCategoryId || job.category_id,
                                job.image_layout || 'single'
                              )}
                              className="w-full"
                              disabled={savingJob === job.id}
                            >
                              {savingJob === job.id ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setSelectedJob(job.id)}
                              className={`flex-1 text-left ${selectedJob === job.id ? "font-semibold" : ""}`}
                            >
                              {job.title}
                            </button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setNewJobCategoryId(""); setEditingJob(job.id); }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteJob(job.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>

                <div className="mt-4 space-y-2">
                  <Input
                    placeholder="Job title"
                    value={newJobTitle}
                    onChange={(e) => setNewJobTitle(e.target.value)}
                  />
                  <Input
                    placeholder="Slug (e.g., galleri-hantverket)"
                    value={newJobSlug}
                    onChange={(e) => setNewJobSlug(e.target.value)}
                  />
                  <Textarea
                    placeholder="Description"
                    value={newJobDescription}
                    onChange={(e) => setNewJobDescription(e.target.value)}
                  />
                  <Button onClick={addJob} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Job
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Select a category</p>
            )}
          </Card>
        </div>
          )}

        {/* Images – stor yta med samma rutnät som jobbet */}
        {selectedJob && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => { setSelectedJob(null); setPanelOpen(false); }}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Alla jobb
              </Button>
              <h2 className="text-xl font-semibold">
                <span className="text-muted-foreground font-normal">
                  {categories.find(c => c.id === jobs.find(j => j.id === selectedJob)?.category_id)?.name} /{" "}
                </span>
                {jobs.find(j => j.id === selectedJob)?.title}
              </h2>
            </div>
            <Button variant="outline" onClick={() => setPanelOpen(true)}>
              <Maximize2 className="w-4 h-4 mr-2" />
              Öppna i stor vy
            </Button>
          </div>
          {imageGridEl(false)}
        </Card>
        )}

        <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
          <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{jobs.find(j => j.id === selectedJob)?.title}</DialogTitle>
            </DialogHeader>
            {imageGridEl(true)}
          </DialogContent>
        </Dialog>

        {/* Add Image Section */}
        {selectedJob && (
          <Card className="p-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">
              Add Images to {jobs.find(j => j.id === selectedJob)?.title}
            </h2>
            
            <div className="space-y-4">
              {selectedJob && jobs.find(j => j.id === selectedJob) && (
                <div className="p-3 bg-muted/50 rounded-md text-sm">
                  <p className="font-medium mb-1">📁 File Location:</p>
                  <code className="text-xs">
                    public/assets/{categories.find(c => c.id === jobs.find(j => j.id === selectedJob)?.category_id)?.slug || 'unknown'}/
                  </code>
                  <p className="text-xs text-muted-foreground mt-2">
                    Place images here and they'll show up automatically. Filename = Caption.
                  </p>
                </div>
              )}
              
              <div 
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  isDragging 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className="text-4xl">📁</div>
                  <div>
                    <p className="text-lg font-medium mb-2">
                      {isDragging ? 'Drop images here' : 'Drag and drop images here'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      or click below to select files
                    </p>
                  </div>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="max-w-md mx-auto cursor-pointer"
                  />
                  {uploadingImage && (
                    <p className="text-sm text-muted-foreground">Adding reference(s)...</p>
                  )}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-2 text-muted-foreground">or add by URL</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Image URL"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                />
                <Input
                  placeholder="Caption"
                  value={newImageCaption}
                  onChange={(e) => setNewImageCaption(e.target.value)}
                />
              </div>
              
              <Button onClick={addImage} className="w-full" disabled={uploadingImage}>
                <Plus className="w-4 h-4 mr-2" />
                Add Image by URL
              </Button>
            </div>
          </Card>
        )}
        </>
        )}

        {activeTab === 'home' && (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Home Gallery Images</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Select images from all categories to display on the home page. Drag to reorder.
            </p>

            <div className="space-y-6">
              {/* Home Images (Checked) */}
              <div>
                <h3 className="text-sm font-medium mb-3">Selected for Home ({images.filter(i => i.show_on_home).length})</h3>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleHomeDragEnd}>
                  <SortableContext 
                    items={images.filter(i => i.show_on_home).sort((a, b) => (a.home_order || 0) - (b.home_order || 0)).map(i => i.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    {images
                      .filter(i => i.show_on_home)
                      .sort((a, b) => (a.home_order || 0) - (b.home_order || 0))
                      .map((img, idx, arr) => {
                        const job = jobs.find(j => j.id === img.job_id);
                        const category = categories.find(c => c.id === job?.category_id);
                        return (
                          <SortableItem
                            key={img.id}
                            id={img.id}
                            onMove={(dir) => moveHomeImage(img.id, dir)}
                            isFirst={idx === 0}
                            isLast={idx === arr.length - 1}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => toggleHomeImage(img.id, false)}
                              className="w-4 h-4"
                            />
                            <img src={img.url} alt={img.caption || ""} className="w-20 h-20 object-cover rounded" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{img.caption}</p>
                              <p className="text-xs text-muted-foreground">
                                {category?.name} / {job?.title}
                              </p>
                            </div>
                          </SortableItem>
                        );
                      })}
                  </SortableContext>
                </DndContext>
              </div>

              {/* All Other Images */}
              <div>
                <h3 className="text-sm font-medium mb-3">All Images</h3>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {images
                    .filter(i => !i.show_on_home)
                    .map(img => {
                      const job = jobs.find(j => j.id === img.job_id);
                      const category = categories.find(c => c.id === job?.category_id);
                      return (
                        <div key={img.id} className="flex items-center gap-3 p-3 bg-card border rounded-md">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => toggleHomeImage(img.id, true)}
                            className="w-4 h-4"
                          />
                          <img src={img.url} alt={img.caption || ""} className="w-20 h-20 object-cover rounded" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{img.caption}</p>
                            <p className="text-xs text-muted-foreground">
                              {category?.name} / {job?.title}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Admin;
